import React, { useState, useEffect } from 'react';
import { FaSearch, FaFilter, FaCheck, FaTimes, FaEye } from 'react-icons/fa';
import { getStudents, updateStudentStatus } from '../../api/studentsApi';
import { useParams } from 'react-router-dom';
import CustomAlertModal from '../../components/common/CustomAlertModal';

const RegisteredStudentsPage = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [alertConfig, setAlertConfig] = useState({ show: false, message: '', onConfirm: null });

  const showCustomAlert = (message, onConfirm = null) => {
    setAlertConfig({ show: true, message, onConfirm });
  };

  const hideCustomAlert = () => {
    const { onConfirm } = alertConfig;
    setAlertConfig({ show: false, message: '', onConfirm: null });
    if (onConfirm) onConfirm();
  };

  const { driveId } = useParams();

  useEffect(() => {
    fetchStudents();
  }, [filterBranch, driveId]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const res = await getStudents({ branch: filterBranch, driveId });
      setStudents(res.data);
    } catch (err) {
      console.error('Failed to fetch students', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    if (window.confirm(`Are you sure you want to mark this student as ${status}?`)) {
      try {
        await updateStudentStatus(id, status);
        fetchStudents(); // refresh
      } catch (err) {
        showCustomAlert('Failed to update status');
      }
    }
  };

  const filteredStudents = students.filter(s => {
    const matchesSearch = (s.fullName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (s.rollNumber || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBranch = filterBranch ? s.branch === filterBranch : true;
    return matchesSearch && matchesBranch;
  });

  return (
    <div className="container-fluid p-0">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <div>
          <h3 className="fw-bold mb-1">Registered Students</h3>
          <p className="text-muted mb-0">Manage candidates and view their statuses.</p>
        </div>
      </div>

      <div className="card custom-card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-4">
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text bg-white"><FaSearch className="text-muted" /></span>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Search by name or roll number..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text bg-white"><FaFilter className="text-muted" /></span>
                <select className="form-select" value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)}>
                  <option value="">All Branches</option>
                  <option value="CSE">Computer Science</option>
                  <option value="IT">Information Tech</option>
                  <option value="ECE">Electronics</option>
                </select>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light text-muted border-bottom-0">
                <tr>
                  <th className="py-3 px-4 fw-semibold border-0 rounded-start">Photo</th>
                  <th className="py-3 px-4 fw-semibold border-0">Name</th>
                  <th className="py-3 px-4 fw-semibold border-0">Roll No.</th>
                  <th className="py-3 px-4 fw-semibold border-0">College & Branch</th>
                  <th className="py-3 px-4 fw-semibold border-0">Contact</th>
                  <th className="py-3 px-4 fw-semibold border-0">Details</th>
                  <th className="py-3 px-4 fw-semibold border-0">Reg. Date</th>
                  <th className="py-3 px-4 fw-semibold border-0">Test Status</th>
                  <th className="py-3 px-4 fw-semibold border-0">Result</th>
                  <th className="py-3 px-4 fw-semibold border-0">Resume</th>
                  <th className="py-3 px-4 fw-semibold border-0 text-end rounded-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="11" className="text-center py-4"><div className="spinner-border text-primary-custom"></div></td></tr>
                ) : filteredStudents.length === 0 ? (
                  <tr><td colSpan="11" className="text-center py-4 text-muted">No students found from backend.</td></tr>
                ) : (
                  filteredStudents.map(s => (
                    <tr key={s.id} className="border-bottom">
                      <td className="py-3 px-4">
                        {s.faceImageUrl ? (
                          <img src={`http://localhost:5000/uploads/${s.faceImageUrl}`} alt="Student Face" className="rounded-circle" style={{ width: '60px', height: '60px', objectFit: 'cover' }} />
                        ) : (
                          <div className="rounded-circle bg-light d-flex align-items-center justify-content-center text-muted border" style={{ width: '60px', height: '60px', fontSize: '0.75rem' }}>No Photo</div>
                        )}
                      </td>
                      <td className="py-3 px-4 fw-bold">{s.fullName}</td>
                      <td className="py-3 px-4 text-muted mono-text">{s.rollNumber}</td>
                      <td className="py-3 px-4 small">
                        <div className="fw-semibold">{s.collegeName}</div>
                        <div className="text-muted">{s.branch}</div>
                      </td>
                      <td className="py-3 px-4 small">
                        <div>{s.email}</div>
                        <div className="text-muted">{s.mobile}</div>
                      </td>
                      <td className="py-3 px-4 small">
                        <div>CGPA: {s.gpa || 'N/A'}</div>
                        <div className="text-muted">Batch: {s.passingYear || 'N/A'}</div>
                      </td>
                      <td className="py-3 px-4">{new Date(s.registrationDate).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <span className={`badge rounded-pill px-2 py-1 ${
                          s.assessmentStatus === 'Completed' ? 'bg-success' :
                          s.assessmentStatus === 'Violated' ? 'bg-danger' :
                          'bg-warning text-dark'
                        }`}>{s.assessmentStatus || 'Pending'}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`badge rounded-pill px-2 py-1 ${
                          s.resultStatus === 'Pass' ? 'bg-success' :
                          s.resultStatus === 'Fail' ? 'bg-danger' :
                          'bg-secondary'
                        }`}>{s.resultStatus || 'Pending'}</span>
                      </td>
                      <td className="py-3 px-4">
                        {s.resumePath ? (
                          <div className="d-flex gap-1">
                            <a href={`http://localhost:5000/uploads/${s.resumePath}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary" title="View Resume">View</a>
                            <a href={`http://localhost:5000/uploads/${s.resumePath}`} download className="btn btn-sm btn-outline-secondary" title="Download Resume">↓</a>
                          </div>
                        ) : (
                          <span className="text-muted small">No Resume</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-end">
                        <div className="d-flex justify-content-end gap-1">
                          <button className="btn btn-sm btn-light text-success" title="Shortlist" onClick={() => handleStatusUpdate(s.id, 'Shortlisted')}><FaCheck /></button>
                          <button className="btn btn-sm btn-light text-danger" title="Reject" onClick={() => handleStatusUpdate(s.id, 'Rejected')}><FaTimes /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <CustomAlertModal show={alertConfig.show} message={alertConfig.message} onHide={hideCustomAlert} />
    </div>
  );
};

export default RegisteredStudentsPage;
